import { useMemo, useRef, useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import koLocale from "@fullcalendar/core/locales/ko";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import GroupsIcon from "@mui/icons-material/Groups";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import SchoolIcon from "@mui/icons-material/School";
import SearchIcon from "@mui/icons-material/Search";
import { toast } from "react-toastify";

import { useProjectApi } from "../../../hooks/useProjectApi";
import { AutoPilotGuide } from "../../../components/common/AutoPilotGuide";
import { EmptyState } from "../../../components/common/EmptyState";

const EDU_TYPES = ["정기", "신규", "특별", "관리감독자", "채용시", "작업변경시"];

const STATUS_OPTIONS = [
  { value: "pending", label: "대기중", color: "warning", icon: <CalendarMonthIcon /> },
  { value: "completed", label: "완료", color: "success", icon: <CheckCircleIcon /> },
  { value: "cancelled", label: "취소", color: "default", icon: <EventBusyIcon /> },
];

const ATTENDEE_STATUS_OPTIONS = [
  { value: "pending", label: "대기", color: "warning" },
  { value: "completed", label: "이수", color: "success" },
  { value: "absent", label: "불참", color: "error" },
];

const TYPE_COLORS = {
  정기: "#2563eb",
  신규: "#0891b2",
  특별: "#ea580c",
  관리감독자: "#7c3aed",
  채용시: "#16a34a",
  작업변경시: "#dc2626",
};

const STATUS_COLORS = {
  pending: "#f59e0b",
  completed: "#16a34a",
  cancelled: "#64748b",
};

const EDUCATION_AUTOPILOT_STEPS = [
  {
    target: '[data-autopilot="education-summary"]',
    title: "교육 현황 확인",
    description:
      "상단 지표에서 전체 일정, 대기중, 완료, 취소, 대상자와 이수율을 먼저 확인합니다. 이 단계는 조회만 하며 데이터를 바꾸지 않습니다.",
  },
  {
    target: '[data-autopilot="education-calendar"]',
    title: "캘린더에서 일정 찾기",
    description:
      "달력에서 날짜별 교육을 확인합니다. 색상은 교육 유형을 구분하고, 일정을 누르면 상세 내용을 볼 수 있습니다.",
  },
  {
    target: '[data-autopilot="education-type-legend"]',
    title: "교육 유형 구분",
    description:
      "정기, 신규, 특별, 관리감독자, 채용시, 작업변경시 교육이 서로 다른 색상으로 표시됩니다.",
  },
  {
    target: '[data-autopilot="education-upcoming"]',
    title: "다가오는 일정 확인",
    description:
      "오른쪽 목록에서 가까운 교육 일정을 빠르게 확인합니다. 클릭하면 상세와 이수 상태를 확인할 수 있습니다.",
  },
  {
    target: '[data-autopilot="education-create"]',
    title: "일정 등록 위치",
    description:
      "웹 화면에서는 이 버튼으로 새 교육 일정을 등록합니다. 모바일에서는 읽기 전용으로 일정만 확인합니다.",
  },
];

const withAlpha = (hex, alpha) => {
  const normalized = hex.replace("#", "");
  const value = parseInt(normalized, 16);
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const getStatusMeta = (status) =>
  STATUS_OPTIONS.find((item) => item.value === status) || STATUS_OPTIONS[0];

const getAttendeeStatusMeta = (status) =>
  ATTENDEE_STATUS_OPTIONS.find((item) => item.value === status) || ATTENDEE_STATUS_OPTIONS[0];

const getToday = () => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const toLocalDateString = (date) => {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const getMonthRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  return {
    start: toLocalDateString(start),
    end: toLocalDateString(end),
  };
};

const makeEmptyForm = (date = getToday()) => ({
  edu_type: "정기",
  title: "",
  edu_date: `${date}T09:00`,
  duration_min: 60,
  location_id: "",
  target_desc: "",
  status: "pending",
  attendees: [],
});

const buildEditForm = (schedule, fallbackDate = getToday()) => ({
  edu_type: schedule.edu_type || "정기",
  title: schedule.title || "",
  edu_date: schedule.edu_date_input || `${fallbackDate}T09:00`,
  duration_min: schedule.duration_min || 60,
  location_id: schedule.location_id || "",
  target_desc: schedule.target_desc || "",
  status: schedule.status || "pending",
  attendees: (schedule.attendees || []).map((item) => ({
    emp_id: Number(item.emp_id),
    status: item.status || "pending",
    completed_at: item.completed_at_input || "",
  })),
});

const createEvents = (schedules) =>
  schedules.map((schedule) => {
    const statusColor = STATUS_COLORS[schedule.status] || STATUS_COLORS.pending;
    const typeColor = TYPE_COLORS[schedule.edu_type] || "#2563eb";
    return {
      id: String(schedule.id),
      title: schedule.title,
      start: schedule.calendar_start,
      end: schedule.calendar_end,
      backgroundColor: schedule.status === "cancelled" ? "#f8fafc" : withAlpha(typeColor, 0.08),
      borderColor: schedule.status === "cancelled" ? statusColor : typeColor,
      textColor: "#111827",
      extendedProps: {
        ...schedule,
        statusColor,
        typeColor,
      },
    };
  });

const buildPayload = (form) => ({
  edu_type: form.edu_type,
  title: form.title,
  edu_date: form.edu_date,
  duration_min: form.duration_min,
  location_id: form.location_id,
  target_desc: form.target_desc,
  status: form.status,
  attendees: form.attendees,
});

function SummaryCard({ icon, label, value, color = "#2563eb", helper }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        height: "100%",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        bgcolor: "#fff",
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Avatar sx={{ bgcolor: `${color}1a`, color }}>{icon}</Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.15 }}>
            {value}
          </Typography>
          {helper ? (
            <Typography variant="caption" color="text.secondary">
              {helper}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Paper>
  );
}

function EducationEventContent({ event }) {
  const schedule = event.extendedProps;
  const completedRate = Number(schedule.target_count)
    ? Math.round((Number(schedule.completed_count || 0) / Number(schedule.target_count || 0)) * 100)
    : 0;

  return (
    <Box sx={{ px: 0.5, py: 0.35, overflow: "hidden" }}>
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <Box
          sx={{
            width: 4,
            height: 18,
            borderRadius: 999,
            bgcolor: schedule.typeColor,
            flex: "0 0 auto",
          }}
        />
        <Typography variant="caption" sx={{ fontWeight: 900, color: "#111827" }} noWrap>
          {event.title}
        </Typography>
      </Stack>
      <Typography variant="caption" sx={{ color: schedule.typeColor, fontWeight: 800, pl: 1.35 }} noWrap>
        {schedule.edu_time} · {schedule.edu_type} · {completedRate}%
      </Typography>
    </Box>
  );
}

function EducationScheduleDialog({
  open,
  mode,
  readOnly,
  form,
  detail,
  employees,
  isLoadingDetail,
  isSaving,
  onClose,
  onChange,
  onSubmit,
  onDelete,
}) {
  const [employeeFilter, setEmployeeFilter] = useState("");
  const isEdit = mode === "edit";

  const attendeeMap = useMemo(
    () => new Map((form.attendees || []).map((item) => [Number(item.emp_id), item])),
    [form.attendees]
  );

  const filteredEmployees = useMemo(() => {
    const keyword = employeeFilter.trim().toLowerCase();
    if (!keyword) return employees;
    return employees.filter((employee) => {
      const text = [
        employee.emp_name,
        employee.department_name,
        employee.position_name,
        employee.emp_id,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return text.includes(keyword);
    });
  }, [employeeFilter, employees]);

  const targetCount = form.attendees?.length || 0;
  const completedCount = (form.attendees || []).filter((item) => item.status === "completed").length;
  const absentCount = (form.attendees || []).filter((item) => item.status === "absent").length;
  const completedRate = targetCount ? Math.round((completedCount / targetCount) * 100) : 0;
  const statusMeta = getStatusMeta(form.status);

  const setEmployeeTarget = (employee, checked) => {
    const empId = Number(employee.emp_id);
    if (checked) {
      onChange("attendees", [...(form.attendees || []), { emp_id: empId, status: "pending" }]);
      return;
    }
    onChange(
      "attendees",
      (form.attendees || []).filter((item) => Number(item.emp_id) !== empId)
    );
  };

  const setAttendeeStatus = (employee, status) => {
    const empId = Number(employee.emp_id);
    onChange(
      "attendees",
      (form.attendees || []).map((item) =>
        Number(item.emp_id) === empId
          ? {
              ...item,
              status,
              completed_at: status === "completed" ? item.completed_at : "",
            }
          : item
      )
    );
  };

  const renderReadOnlyContent = () => (
    <Stack spacing={2}>
      <Paper elevation={0} sx={{ p: 2, border: "1px solid", borderColor: "divider" }}>
        <Stack spacing={1.5}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
            <Box>
              <Chip
                size="small"
                label={form.edu_type}
                sx={{ bgcolor: `${TYPE_COLORS[form.edu_type] || "#2563eb"}1a`, color: TYPE_COLORS[form.edu_type] || "#2563eb", fontWeight: 800 }}
              />
              <Typography variant="h6" sx={{ fontWeight: 900, mt: 1 }}>
                {form.title}
              </Typography>
            </Box>
            <Chip size="small" color={statusMeta.color} label={statusMeta.label} />
          </Stack>
          <Divider />
          <Grid container spacing={1.5}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                교육 일시
              </Typography>
              <Typography sx={{ fontWeight: 800 }}>{detail?.edu_day} {detail?.edu_time}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                교육 시간
              </Typography>
              <Typography sx={{ fontWeight: 800 }}>{form.duration_min || "-"}분</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                대상
              </Typography>
              <Typography sx={{ fontWeight: 800 }}>{form.target_desc || "-"}</Typography>
            </Grid>
          </Grid>
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ p: 2, border: "1px solid", borderColor: "divider" }}>
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography sx={{ fontWeight: 900 }}>이수 현황</Typography>
          <Typography sx={{ fontWeight: 900 }}>{completedRate}%</Typography>
        </Stack>
        <LinearProgress variant="determinate" value={completedRate} sx={{ height: 10, borderRadius: 999 }} />
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
          <Chip size="small" icon={<GroupsIcon />} label={`대상 ${targetCount}명`} />
          <Chip size="small" color="success" label={`이수 ${completedCount}명`} />
          <Chip size="small" color="error" label={`불참 ${absentCount}명`} />
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ p: 2, border: "1px solid", borderColor: "divider" }}>
        <Typography sx={{ fontWeight: 900, mb: 1 }}>대상자</Typography>
        <List disablePadding>
          {(detail?.attendees || []).map((attendee) => {
            const attendeeStatus = getAttendeeStatusMeta(attendee.status);
            return (
              <ListItem key={attendee.emp_id} divider sx={{ px: 0 }}>
                <ListItemText
                  primary={attendee.emp_name || attendee.emp_id}
                  secondary={[attendee.department_name, attendee.position_name]
                    .filter(Boolean)
                    .join(" / ")}
                />
                <Chip size="small" color={attendeeStatus.color} label={attendeeStatus.label} />
              </ListItem>
            );
          })}
          {!detail?.attendees?.length ? (
            <ListItem sx={{ px: 0 }}>
              <ListItemText primary="등록된 대상자가 없습니다." />
            </ListItem>
          ) : null}
        </List>
      </Paper>
    </Stack>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={readOnly}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              {readOnly ? "교육 일정 상세" : isEdit ? "교육 일정 수정" : "교육 일정 등록"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {readOnly ? "모바일에서는 읽기 전용으로 제공됩니다." : "교육 일정과 대상자 이수 상태를 함께 관리합니다."}
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: "#f7f8fb" }}>
        {isLoadingDetail ? (
          <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 360 }}>
            <CircularProgress />
          </Stack>
        ) : readOnly ? (
          renderReadOnlyContent()
        ) : (
          <Grid container spacing={2.25}>
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 2, border: "1px solid", borderColor: "divider" }}>
                <Typography sx={{ fontWeight: 900, mb: 2 }}>교육 정보</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={5}>
                    <FormControl fullWidth>
                      <InputLabel>교육 유형</InputLabel>
                      <Select
                        label="교육 유형"
                        value={form.edu_type}
                        onChange={(event) => onChange("edu_type", event.target.value)}
                      >
                        {EDU_TYPES.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={7}>
                    <FormControl fullWidth>
                      <InputLabel>상태</InputLabel>
                      <Select
                        label="상태"
                        value={form.status}
                        onChange={(event) => onChange("status", event.target.value)}
                      >
                        {STATUS_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      label="교육명"
                      value={form.title}
                      onChange={(event) => onChange("title", event.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={7}>
                    <TextField
                      fullWidth
                      required
                      type="datetime-local"
                      label="교육 일자"
                      value={form.edu_date}
                      onChange={(event) => onChange("edu_date", event.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <TextField
                      fullWidth
                      type="number"
                      label="교육 시간(분)"
                      inputProps={{ min: 1 }}
                      value={form.duration_min}
                      onChange={(event) => onChange("duration_min", event.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="대상 설명"
                      placeholder="예: 조립 C라인 전체"
                      value={form.target_desc}
                      onChange={(event) => onChange("target_desc", event.target.value)}
                    />
                  </Grid>
                </Grid>
              </Paper>

              <Paper
                elevation={0}
                sx={{ p: 2, mt: 2, border: "1px solid", borderColor: "divider" }}
              >
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography sx={{ fontWeight: 900 }}>이수율</Typography>
                  <Typography sx={{ fontWeight: 900 }}>{completedRate}%</Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={completedRate}
                  sx={{ height: 10, borderRadius: 999 }}
                />
                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                  <Chip size="small" icon={<GroupsIcon />} label={`대상 ${targetCount}명`} />
                  <Chip size="small" color="success" label={`이수 ${completedCount}명`} />
                  <Chip size="small" color="error" label={`불참 ${absentCount}명`} />
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 2, border: "1px solid", borderColor: "divider" }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "stretch", sm: "center" }}
                  spacing={1.5}
                  sx={{ mb: 1.5 }}
                >
                  <Typography sx={{ fontWeight: 900 }}>교육 대상자</Typography>
                  <TextField
                    size="small"
                    placeholder="직원 검색"
                    value={employeeFilter}
                    onChange={(event) => setEmployeeFilter(event.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Stack>

                <List
                  disablePadding
                  sx={{
                    maxHeight: 560,
                    overflow: "auto",
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "#fff",
                  }}
                >
                  {filteredEmployees.map((employee) => {
                    const attendee = attendeeMap.get(Number(employee.emp_id));
                    const selected = Boolean(attendee);
                    return (
                      <ListItem
                        key={employee.emp_id}
                        divider
                        secondaryAction={
                          <FormControl size="small" sx={{ minWidth: 104 }}>
                            <Select
                              value={attendee?.status || "pending"}
                              disabled={!selected}
                              onChange={(event) => setAttendeeStatus(employee, event.target.value)}
                            >
                              {ATTENDEE_STATUS_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        }
                      >
                        <Checkbox
                          checked={selected}
                          onChange={(event) => setEmployeeTarget(employee, event.target.checked)}
                        />
                        <ListItemText
                          primary={employee.emp_name || employee.emp_id}
                          secondary={[employee.department_name, employee.position_name]
                            .filter(Boolean)
                            .join(" / ")}
                          sx={{ pr: 13 }}
                        />
                      </ListItem>
                    );
                  })}
                  {!filteredEmployees.length ? (
                    <ListItem>
                      <ListItemText primary="직원 데이터가 없습니다." />
                    </ListItem>
                  ) : null}
                </List>
              </Paper>
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {!readOnly && isEdit ? (
          <Button
            color="error"
            startIcon={<DeleteIcon />}
            onClick={onDelete}
            sx={{ mr: "auto" }}
          >
            삭제
          </Button>
        ) : null}
        <Button onClick={onClose} color="inherit">
          {readOnly ? "닫기" : "취소"}
        </Button>
        {!readOnly ? (
          <Button
            variant="contained"
            startIcon={<SchoolIcon />}
            disabled={isSaving || isLoadingDetail}
            onClick={onSubmit}
          >
            {isEdit ? "수정 완료" : "등록 완료"}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}

function MobileTodayList({ schedules, selectedDay, onSelectDay, onOpen }) {
  const daySchedules = schedules.filter((schedule) => schedule.edu_day === selectedDay);

  return (
    <Stack spacing={1.25}>
      <TextField
        fullWidth
        type="date"
        label="일자"
        value={selectedDay}
        onChange={(event) => onSelectDay(event.target.value)}
        InputLabelProps={{ shrink: true }}
      />
      {daySchedules.map((schedule) => {
        const status = getStatusMeta(schedule.status);
        const completedRate = Number(schedule.target_count)
          ? Math.round((Number(schedule.completed_count || 0) / Number(schedule.target_count || 0)) * 100)
          : 0;
        return (
          <Card
            key={schedule.id}
            variant="outlined"
            sx={{ borderRadius: 2, cursor: "pointer" }}
            onClick={() => onOpen(schedule)}
          >
            <CardContent>
              <Stack spacing={1.25}>
                <Stack direction="row" justifyContent="space-between" spacing={1}>
                  <Box sx={{ minWidth: 0 }}>
                    <Chip
                      size="small"
                      label={schedule.edu_type}
                      sx={{
                        mb: 1,
                        color: TYPE_COLORS[schedule.edu_type] || "#2563eb",
                        bgcolor: `${TYPE_COLORS[schedule.edu_type] || "#2563eb"}1a`,
                        fontWeight: 800,
                      }}
                    />
                    <Typography sx={{ fontWeight: 900 }} noWrap>
                      {schedule.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {schedule.edu_time} · {schedule.duration_min || 60}분
                    </Typography>
                  </Box>
                  <Chip size="small" color={status.color} label={status.label} />
                </Stack>
                <LinearProgress variant="determinate" value={completedRate} sx={{ height: 8, borderRadius: 999 }} />
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip size="small" icon={<GroupsIcon />} label={`대상 ${schedule.target_count || 0}명`} />
                  <Chip size="small" color="success" label={`이수 ${schedule.completed_count || 0}명`} />
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        );
      })}
      {!daySchedules.length ? (
        <EmptyState
          compact
          icon={<SchoolIcon />}
          title="선택한 날짜의 교육 일정이 없습니다"
          description="모바일에서는 읽기 전용으로 일정을 확인합니다. 등록은 웹 화면에서 진행할 수 있습니다."
        />
      ) : null}
    </Stack>
  );
}

export function EducationScheduleManagement() {
  const { group_code, project_code } = useParams();
  const { safeAppMode } = useOutletContext() || {};
  const api = useProjectApi(group_code, project_code);
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isWorkerMode = safeAppMode === "worker";
  const isReadOnlyMode = isMobile || isWorkerMode;
  const calendarRef = useRef(null);

  const [range, setRange] = useState(getMonthRange());
  const [selectedDay, setSelectedDay] = useState(getToday());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("create");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(makeEmptyForm());
  const [detail, setDetail] = useState(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [autopilotOpen, setAutopilotOpen] = useState(false);
  const [autopilotStepIndex, setAutopilotStepIndex] = useState(0);

  const optionsQuery = useQuery({
    queryKey: ["risk-edu-options", group_code, project_code],
    queryFn: async () => {
      const response = await api.get("/edu/options");
      return response.data || { employees: [] };
    },
    enabled: Boolean(group_code && project_code),
    placeholderData: { employees: [] },
  });

  const schedulesQuery = useQuery({
    queryKey: ["risk-edu-schedules", group_code, project_code, range.start, range.end],
    queryFn: async () => {
      const response = await api.get("/edu/schedules", {
        params: {
          start: range.start,
          end: range.end,
        },
      });
      return response.data || [];
    },
    enabled: Boolean(group_code && project_code && range.start && range.end),
    placeholderData: [],
  });

  const employees = useMemo(() => optionsQuery.data?.employees || [], [optionsQuery.data]);
  const schedules = useMemo(() => schedulesQuery.data || [], [schedulesQuery.data]);
  const events = useMemo(() => createEvents(schedules), [schedules]);

  const invalidateSchedules = () => {
    queryClient.invalidateQueries({
      queryKey: ["risk-edu-schedules", group_code, project_code, range.start, range.end],
    });
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = buildPayload(form);
      if (dialogMode === "edit") {
        return api.put(`/edu/schedules/${editingId}`, payload);
      }
      return api.post("/edu/schedules", payload);
    },
    onSuccess: () => {
      toast.success(dialogMode === "edit" ? "교육 일정이 저장되었습니다." : "교육 일정이 등록되었습니다.");
      closeDialog();
      invalidateSchedules();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || "교육 일정 저장 중 오류가 발생했습니다.");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (ids) => api.delete("/edu/schedules", { data: { ids } }),
    onSuccess: () => {
      toast.success("교육 일정이 삭제되었습니다.");
      closeDialog();
      invalidateSchedules();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || "교육 일정 삭제 중 오류가 발생했습니다.");
    },
  });

  const summary = useMemo(() => {
    const totalTarget = schedules.reduce((sum, item) => sum + Number(item.target_count || 0), 0);
    const totalCompleted = schedules.reduce((sum, item) => sum + Number(item.completed_count || 0), 0);
    const pending = schedules.filter((item) => item.status === "pending").length;
    const completed = schedules.filter((item) => item.status === "completed").length;
    const cancelled = schedules.filter((item) => item.status === "cancelled").length;
    const rate = totalTarget ? Math.round((totalCompleted / totalTarget) * 100) : 0;
    return { total: schedules.length, pending, completed, cancelled, totalTarget, totalCompleted, rate };
  }, [schedules]);

  const openCreateDialog = (dateInput = `${selectedDay}T09:00`) => {
    if (isMobile) return;
    const nextForm = makeEmptyForm(dateInput.slice(0, 10));
    setDialogMode("create");
    setEditingId(null);
    setDetail(null);
    setForm({ ...nextForm, edu_date: dateInput });
    setDialogOpen(true);
  };

  const openScheduleDialog = async (schedule, readOnlyMode = isReadOnlyMode) => {
    setDialogMode(readOnlyMode ? "read" : "edit");
    setEditingId(schedule.id);
    setDialogOpen(true);
    setIsLoadingDetail(true);

    try {
      const response = await api.get(`/edu/schedules/${schedule.id}`);
      setDetail(response.data);
      setForm(buildEditForm(response.data, schedule.edu_day));
    } catch (error) {
      toast.error(error?.response?.data?.message || "교육 일정 상세 조회 중 오류가 발생했습니다.");
      setDetail(schedule);
      setForm(buildEditForm(schedule, schedule.edu_day));
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setDialogMode("create");
    setEditingId(null);
    setDetail(null);
    setIsLoadingDetail(false);
    setForm(makeEmptyForm(selectedDay));
  };

  const handleFormChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    if (!String(form.title || "").trim()) {
      toast.warning("교육명을 입력해주세요.");
      return;
    }
    if (!form.edu_date) {
      toast.warning("교육 일자를 입력해주세요.");
      return;
    }
    saveMutation.mutate();
  };

  const handleDelete = () => {
    if (!editingId) return;
    if (window.confirm("이 교육 일정을 삭제할까요?")) {
      deleteMutation.mutate([editingId]);
    }
  };

  const handleDatesSet = (info) => {
    const start = info.startStr.slice(0, 10);
    const end = info.endStr.slice(0, 10);
    setRange((prev) => (prev.start === start && prev.end === end ? prev : { start, end }));
  };

  const handleDateClick = (info) => {
    setSelectedDay(info.dateStr);
    if (!isReadOnlyMode) {
      openCreateDialog(`${info.dateStr}T09:00`);
    }
  };

  const handleEventClick = (info) => {
    const schedule = info.event.extendedProps;
    openScheduleDialog(schedule, isReadOnlyMode);
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, height: "100%", minHeight: 0, overflow: "auto", bgcolor: "#f7f8fb" }}>
      <Stack spacing={2.5} sx={{ minHeight: "100%" }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            bgcolor: "#fff",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", md: "center" }}
          >
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <SchoolIcon color="primary" />
                <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 900 }}>
                  근로자 안전보건교육 관리
                </Typography>
              </Stack>
            </Box>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <Tooltip title="오토파일럿">
                <IconButton
                  color="primary"
                  onClick={() => {
                    setAutopilotStepIndex(0);
                    setAutopilotOpen(true);
                  }}
                  sx={{
                    alignSelf: { xs: "flex-start", sm: "center" },
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                  }}
                >
                  <PlayCircleOutlineIcon />
                </IconButton>
              </Tooltip>
              {!isReadOnlyMode ? (
                <Button data-autopilot="education-create" variant="contained" startIcon={<AddIcon />} onClick={() => openCreateDialog()}>
                  일정 등록
                </Button>
              ) : null}
            </Stack>
          </Stack>
        </Paper>

        <Grid container spacing={1.5} data-autopilot="education-summary">
          <Grid item xs={6} md={2}>
            <SummaryCard icon={<MenuBookIcon />} label="전체 일정" value={`${summary.total}건`} />
          </Grid>
          <Grid item xs={6} md={2}>
            <SummaryCard icon={<CalendarMonthIcon />} label="대기중" value={`${summary.pending}건`} color="#f59e0b" />
          </Grid>
          <Grid item xs={6} md={2}>
            <SummaryCard icon={<CheckCircleIcon />} label="완료" value={`${summary.completed}건`} color="#16a34a" />
          </Grid>
          <Grid item xs={6} md={2}>
            <SummaryCard icon={<EventBusyIcon />} label="취소" value={`${summary.cancelled}건`} color="#64748b" />
          </Grid>
          <Grid item xs={6} md={2}>
            <SummaryCard icon={<GroupsIcon />} label="대상자" value={`${summary.totalTarget}명`} />
          </Grid>
          <Grid item xs={6} md={2}>
            <SummaryCard
              icon={<SchoolIcon />}
              label="이수율"
              value={`${summary.rate}%`}
              color="#16a34a"
            />
          </Grid>
        </Grid>

        {schedulesQuery.isError ? (
          <Alert severity="error">교육 일정 데이터를 불러오는 중 오류가 발생했습니다.</Alert>
        ) : null}

        <Grid container spacing={2} sx={{ flex: 1, minHeight: 0 }}>
          <Grid item xs={12} lg={isMobile ? 12 : 9.5}>
            <Paper
              data-autopilot="education-calendar"
              elevation={0}
              sx={{
                p: { xs: 1, md: 2 },
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                bgcolor: "#fff",
                height: { xs: 540, md: "calc(100vh - 268px)" },
                minHeight: { xs: 520, md: 640 },
                position: "relative",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                "& .fc": {
                  fontFamily: "inherit",
                  flex: 1,
                  minHeight: 0,
                },
                "& .fc-toolbar-title": {
                  fontSize: { xs: "1.05rem", md: "1.35rem" },
                  fontWeight: 900,
                },
                "& .fc-header-toolbar": {
                  flexDirection: { xs: "column", sm: "row" },
                  gap: { xs: 1, sm: 0 },
                  alignItems: { xs: "stretch", sm: "center" },
                },
                "& .fc-toolbar-chunk": {
                  display: "flex",
                  justifyContent: { xs: "center", sm: "initial" },
                  flexWrap: "wrap",
                  gap: 0.5,
                },
                "& .fc-button": {
                  height: "34px",
                  borderRadius: "9px !important",
                  textTransform: "none",
                  fontWeight: 800,
                  fontSize: "0.82rem",
                  padding: "0 12px !important",
                  boxShadow: "none !important",
                },
                "& .fc-prev-button, & .fc-next-button": {
                  minWidth: "34px",
                  width: "34px",
                  padding: "0 !important",
                },
                "& .fc-button-primary": {
                  bgcolor: "#fff !important",
                  borderColor: "#dbe3ee !important",
                  color: "#334155 !important",
                },
                "& .fc-button-primary:not(:disabled):hover": {
                  bgcolor: "#f8fafc !important",
                  borderColor: "#b9c4d4 !important",
                  color: "#0f172a !important",
                },
                "& .fc-button-primary:not(:disabled).fc-button-active": {
                  bgcolor: "#0f172a !important",
                  borderColor: "#0f172a !important",
                  color: "#fff !important",
                },
                "& .fc-button-primary:disabled": {
                  bgcolor: "#eef2f7 !important",
                  borderColor: "#e2e8f0 !important",
                  color: "#94a3b8 !important",
                  opacity: "1 !important",
                },
                "& .fc-daygrid-event": {
                  borderRadius: "7px",
                  borderWidth: "1px",
                  borderStyle: "solid",
                  overflow: "hidden",
                  boxShadow: "0 2px 8px rgba(15, 23, 42, 0.06)",
                },
                "& .fc-event-main": {
                  color: "#111827",
                },
                "& .fc-daygrid-day-number": {
                  color: "text.primary",
                  fontWeight: 700,
                },
              }}
            >
              {schedulesQuery.isFetching ? (
                <LinearProgress sx={{ position: "absolute", left: 0, right: 0, top: 0, zIndex: 2 }} />
              ) : null}
              <FullCalendar
                ref={calendarRef}
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                locale={koLocale}
                initialView={isMobile ? "dayGridMonth" : "dayGridMonth"}
                headerToolbar={
                  isMobile
                    ? {
                        left: "prev,next",
                        center: "title",
                        right: "today",
                      }
                    : {
                        left: "prev,next today",
                        center: "title",
                        right: "dayGridMonth,timeGridWeek,timeGridDay",
                      }
                }
                buttonText={{
                  today: "오늘",
                  month: "월",
                  week: "주",
                  day: "일",
                }}
                events={events}
                datesSet={handleDatesSet}
                dateClick={handleDateClick}
                eventClick={handleEventClick}
                eventContent={(eventInfo) => <EducationEventContent event={eventInfo.event} />}
                selectable={!isMobile}
                selectMirror={!isMobile}
                dayMaxEvents={isMobile ? 2 : 4}
                height={isMobile ? 520 : "100%"}
                eventTimeFormat={{
                  hour: "2-digit",
                  minute: "2-digit",
                  meridiem: false,
                }}
              />
            </Paper>
          </Grid>

          {!isMobile ? (
            <Grid item xs={12} lg={2.5}>
              <Paper
                data-autopilot="education-type-legend"
                elevation={0}
                sx={{
                  p: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  bgcolor: "#fff",
                  height: "100%",
                  minHeight: { md: 640 },
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <Typography sx={{ fontWeight: 900, mb: 1 }}>교육 유형</Typography>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  {EDU_TYPES.map((type) => (
                    <Stack
                      key={type}
                      direction="row"
                      alignItems="center"
                      spacing={0.6}
                      sx={{
                        px: 0.9,
                        py: 0.45,
                        border: "1px solid",
                        borderColor: withAlpha(TYPE_COLORS[type], 0.22),
                        borderRadius: 999,
                        bgcolor: withAlpha(TYPE_COLORS[type], 0.08),
                      }}
                    >
                      <Box
                        sx={{
                          width: 7,
                          height: 7,
                          borderRadius: "50%",
                          bgcolor: TYPE_COLORS[type],
                          flex: "0 0 auto",
                        }}
                      />
                      <Typography variant="caption" sx={{ color: TYPE_COLORS[type], fontWeight: 900 }}>
                        {type}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>

                <Divider sx={{ my: 1.5 }} />

                <Typography sx={{ fontWeight: 900, mb: 1.5 }}>다가오는 일정</Typography>
                <Stack data-autopilot="education-upcoming" spacing={1} sx={{ flex: 1, minHeight: 0, overflow: "auto", pr: 0.5 }}>
                  {schedules.slice(0, 8).map((schedule) => {
                    const status = getStatusMeta(schedule.status);
                    const typeColor = TYPE_COLORS[schedule.edu_type] || "#2563eb";
                    return (
                      <Paper
                        key={schedule.id}
                        variant="outlined"
                        sx={{
                          p: 1.25,
                          cursor: "pointer",
                          borderColor: "divider",
                          borderLeftWidth: 4,
                          borderLeftStyle: "solid",
                          borderLeftColor: typeColor,
                          bgcolor: withAlpha(typeColor, 0.04),
                          transition: "border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease",
                          "&:hover": {
                            borderColor: typeColor,
                            boxShadow: "0 8px 22px rgba(15, 23, 42, 0.08)",
                            transform: "translateY(-1px)",
                          },
                        }}
                        onClick={() => openScheduleDialog(schedule, isReadOnlyMode)}
                      >
                        <Stack spacing={0.75}>
                          <Stack direction="row" justifyContent="space-between" spacing={1}>
                            <Typography variant="body2" sx={{ fontWeight: 900 }} noWrap>
                              {schedule.title}
                            </Typography>
                            <Chip size="small" color={status.color} label={status.label} />
                          </Stack>
                          <Chip
                            size="small"
                            label={schedule.edu_type}
                            sx={{
                              alignSelf: "flex-start",
                              height: 22,
                              bgcolor: withAlpha(typeColor, 0.12),
                              color: typeColor,
                              fontWeight: 900,
                              border: `1px solid ${withAlpha(typeColor, 0.22)}`,
                            }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {schedule.edu_day} {schedule.edu_time}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            대상 {schedule.target_count || 0}명 · 이수 {schedule.completed_count || 0}명
                          </Typography>
                        </Stack>
                      </Paper>
                    );
                  })}
                  {!schedules.length ? (
                    <EmptyState
                      compact
                      icon={<SchoolIcon />}
                      title="표시할 교육 일정이 없습니다"
                      description="일정 등록 버튼으로 교육명, 대상자, 이수 상태를 등록하면 캘린더와 목록에 함께 표시됩니다."
                      actionLabel="일정 등록"
                      actionIcon={<AddIcon />}
                      onAction={() => openCreateDialog()}
                    />
                  ) : null}
                </Stack>
              </Paper>
            </Grid>
          ) : (
            <Grid item xs={12}>
              <MobileTodayList
                schedules={schedules}
                selectedDay={selectedDay}
                onSelectDay={setSelectedDay}
                onOpen={(schedule) => openScheduleDialog(schedule, true)}
              />
            </Grid>
          )}
        </Grid>
      </Stack>

      <EducationScheduleDialog
        open={dialogOpen}
        mode={dialogMode}
        readOnly={isReadOnlyMode || dialogMode === "read"}
        form={form}
        detail={detail}
        employees={employees}
        isLoadingDetail={isLoadingDetail}
        isSaving={saveMutation.isPending}
        onClose={closeDialog}
        onChange={handleFormChange}
        onSubmit={handleSubmit}
        onDelete={handleDelete}
      />
      <AutoPilotGuide
        open={autopilotOpen}
        steps={EDUCATION_AUTOPILOT_STEPS}
        stepIndex={autopilotStepIndex}
        onStepChange={setAutopilotStepIndex}
        onClose={() => setAutopilotOpen(false)}
      />
    </Box>
  );
}
